import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { API } from 'constants';
import client from '../../../../services/cycle-api';

import { mutateAllCachedRequest } from '../../../../helpers/swrMutateUtils';

import { useSwrRequestKeysToMutateFromContext } from '../../../../hooks/useSwrRequestKeysToMutateFromContext';

import { defaultErrorMessage } from '../../../../constants/errorMessages';

import Modal from '../../../../design-system/Modal/Modal';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import ModalHeader from '../../../../design-system/ModalHeader/ModalHeader';
import FormFieldWrapper from '../../../../design-system/FormFieldWrapper/FormFieldWrapper';
import { Button } from '../../../../design-system';

const RequestPlaybookModal = ({ templateId, templateName, templateDescription, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [selectedOption, setSelectedOption] = useState('current');

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const {
        requestKeysToMutate: { cycles: requestKeysToMutate },
    } = useSwrRequestKeysToMutateFromContext();

    const onSubmit = async () => {
        try {
            setIsLoading(true);

            const requestBody = {
                name: templateName,
                description: templateDescription,
                template: null,
            };

            if (selectedOption === 'current') {
                requestBody.template = templateId;
            }

            const { data } = await client.post(API.ROUTES.cycle.cycle, requestBody);

            void mutateAllCachedRequest({ requestKeysToMutate });

            const cycleId = data.id;
            navigate(`/cycle/${cycleId}`, { state: { from: location } });
        } catch (e) {
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal size="mediumPlus" resetPadding onClose={onClose}>
            <ModalHeader
                title="Request Playbook"
                outlined
                onClose={onClose}
                customPaddings="px-5 py-3"
            />

            <div className="p-5">
                <FormFieldWrapper label="Select how you’d like to start this CRAFT cycle...">
                    <div className="flex flex-col xs:flex-row gap-2">
                        <Option
                            title="Use This Playbook Template"
                            description={`Start a CRAFT cycle for your own ${templateName}.`}
                            isSelected={selectedOption === 'current'}
                            handleSelect={() => setSelectedOption('current')}
                        />
                        <Option
                            title="Request a Custom Playbook"
                            description="Start a CRAFT cycle for a custom playbook from scratch."
                            isSelected={selectedOption === 'custom'}
                            handleSelect={() => setSelectedOption('custom')}
                        />
                    </div>
                </FormFieldWrapper>
            </div>

            <div className="px-5 py-3 flex items-center justify-between gap-3">
                <Button type="neutral" size="sm" text="Cancel" onClick={onClose} />
                <Button
                    type="secondary"
                    size="sm"
                    text="Continue"
                    state={isLoading ? 'loading' : 'default'}
                    onClick={onSubmit}
                />
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Modal>
    );
};

const Option = ({ title, description, isSelected = false, handleSelect }) => {
    const className = classNames(
        'flex-1 flex flex-col gap-0.5 p-3 rounded-2 border-1 shadow-inset-1 transition-colors',
        {
            'border-purple-500 shadow-purple-500': isSelected,
            'border-neutral-200 shadow-transparent': !isSelected,
        }
    );

    return (
        <button className={className} onClick={handleSelect}>
            <p className="font-body-bold text-body-bold-s text-neutral-500 text-start">{title}</p>
            <p className="font-body text-body-regular-s italic text-black text-start">
                {description}
            </p>
        </button>
    );
};

export default RequestPlaybookModal;
